import { useNavigate } from 'react-router';
import { useAdmin } from './adminContext';
import { RequirePermission } from './RequirePermission';
import { ResourcePage } from './ResourcePage';
import type { ResourceDef } from './resources';
import { usesFormPage } from './formPage';
import { detailRoute, newRecordRoute } from './routes';

/**
 * A top-level resource's list. Records open their own page when it has more than the form: children, lists of records that
 * point to it (`tabOf`) or extra tabs; or when the resource is edited on its own page. `fixed`: a list of only the records with
 * these values (e.g. pages of kind form), whose new records get them.
 */
export function ListScreen({ def, fixed }: { def: ResourceDef; fixed?: Readonly<Record<string, string>> }) {
  const { session, extensions, resources } = useAdmin();
  const toolbar = extensions[def.key]?.listToolbar;
  const quickFilters = extensions[def.key]?.quickFilters;
  const formPage = usesFormPage(def, extensions[def.key]);
  const ownPage =
    formPage ||
    def.children.length > 0 ||
    (extensions[def.key]?.tabs?.length ?? 0) > 0 ||
    Object.values(resources).some((other) => other.tabOf?.some((tab) => tab.parent === def.key));
  const navigate = useNavigate();
  return (
    <RequirePermission session={session} permission={def.readPermission}>
      <ResourcePage
        def={def}
        onOpen={ownPage ? (r) => void navigate(detailRoute(def.key, r.id)) : undefined}
        onCreate={formPage ? () => void navigate(newRecordRoute(def.key, undefined, fixed)) : undefined}
        toolbar={toolbar}
        quickFilters={quickFilters}
        fixed={fixed}
      />
    </RequirePermission>
  );
}
